import React from "react";
import { faEye, faUpload } from "@fortawesome/free-solid-svg-icons";
import NiceModal from "@ebay/nice-modal-react";
import KC_Button, {
  KC_ButtonSize,
  KC_ButtonType
} from "../../../../../../designsystem/KC_Button/KC_Button";
import KC_Text from "../../../../../../designsystem/typography/KC_Text/KC_Text";
import {
  KC_Text_Sizes,
  KC_Weights
} from "../../../../../../designsystem/ds_commons";
import KC_Heading4 from "../../../../../../designsystem/typography/KC-Heading/KC_Heading4";

import { setLoading } from "../../../../../../redux/actions/app";
import UploadedPOSA, {
  POSAOptionType,
  Status
} from "@tootsweet/model/entities/UploadedPOSA";

import POSAApi from "../../../../../12_POSAManager/api";
import { useDispatch } from "react-redux";
import { useAccountToDisplaySelector } from "../../../../../../redux/actions/user";
import { tt } from "../../../../../../utils/i18n/i18n";
import { TranslationsKeys } from "../../../../../../utils/i18n/translations/Translations";
import PreviewModal from "./PreviewModal";

interface IProps {
  posaOption: POSAOptionType;
  uploadedPOSAList: UploadedPOSA[];
  onRefresh: () => Promise<void>;
}

export default function UploadExistingPOSA(props: IProps) {
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();

  async function uploadFile(name: string, file: File): Promise<void> {
    dispatch(setLoading(true));

    try {
      await POSAApi.uploadExistingPOSA(
        accountToDisplay?.trackingID,
        name,
        file,
        accountToDisplay?.token
      );

      props.onRefresh();
    } catch (e) {
      console.error(e);
    } finally {
      dispatch(setLoading(false));
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e?.target?.files?.[0];
    if (file != null) {
      void uploadFile(props.posaOption.format, file);
    }
  }

  const displayName = props.posaOption.displayName;

  const currentUploadedPOSA = props.uploadedPOSAList.find(
    (item) => item.type === props.posaOption.format
  );

  return (
    <div className="d-flex flex-column upload-file p-4">
      <div className="d-flex align-items-center">
        <KC_Heading4 text={displayName} />

        {currentUploadedPOSA?.status && (
          <span
            className="ml-4 px-3 py-1"
            style={{
              backgroundColor: STATUS_COLORS[currentUploadedPOSA.status].bg,
              color: STATUS_COLORS[currentUploadedPOSA.status].text,
              borderRadius: "16px",
              fontSize: "14px",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              whiteSpace: "nowrap"
            }}
          >
            {tt(STATUS_TRANSLATION_KEYS[currentUploadedPOSA.status])}
          </span>
        )}
      </div>

      <div className="d-flex">
        <KC_Text
          text={currentUploadedPOSA?.inputImage.name}
          size={KC_Text_Sizes.md}
          weight={KC_Weights.reg}
          className="d-flex align-items-center mr-auto"
        />
        {!currentUploadedPOSA?.outputImage && (
          <KC_Button
            isInverted
            size={KC_ButtonSize.Medium}
            type={KC_ButtonType.Button}
            icon={faUpload}
            showIconOnTheLeft
            text={tt("posa_form_upload_support")}
            onClick={() => {
              const input = document.getElementById(
                `upload-existing-posa-${props.posaOption.format}`
              ) as HTMLInputElement;
              if (input) {
                input.click();
              }
            }}
            extraStyle={{ width: "min-content" }}
            className="ml-auto"
          />
        )}
        {currentUploadedPOSA &&
          [Status.PENDING_CLIENT, Status.DONE].includes(
            currentUploadedPOSA.status
          ) && (
            <KC_Button
              isInverted
              icon={faEye}
              showIconOnTheLeft
              size={KC_ButtonSize.Medium}
              type={KC_ButtonType.Button}
              text={tt("posa_form_view_result")}
              onClick={() => {
                void NiceModal.show(PreviewModal, {
                  uploadedPOSA: currentUploadedPOSA,
                  onRefresh: props.onRefresh
                });
              }}
              extraStyle={{ width: "min-content" }}
            />
          )}
      </div>
      <input
        id={`upload-existing-posa-${props.posaOption.format}`}
        className="d-none"
        onChange={handleChange}
        type="file"
        formEncType="multipart-formdata"
        accept="image/png,image/jpeg,image/webp,.pdf"
      />
    </div>
  );
}

const STATUS_TRANSLATION_KEYS: Record<Status, keyof TranslationsKeys> = {
  [Status.CREATED]: "posa_form_status_created",
  [Status.IN_PROGRESS]: "posa_form_status_in_progress",
  [Status.PENDING_CLIENT]: "posa_form_status_pending_client",
  [Status.PENDING_AM]: "posa_form_status_pending_am",
  [Status.REJECTED_BY_TEAM]: "posa_form_status_rejected_by_team",
  [Status.RESENT]: "posa_form_status_resent",
  [Status.DONE]: "posa_form_status_done"
};

const STATUS_COLORS: Record<Status, { bg: string; text: string }> = {
  [Status.CREATED]: { bg: "#E3F2FD", text: "#1565C0" },
  [Status.IN_PROGRESS]: { bg: "#FFF3E0", text: "#E65100" },
  [Status.PENDING_CLIENT]: { bg: "#FFF9C4", text: "#F57F17" },
  [Status.PENDING_AM]: { bg: "#F3E5F5", text: "#6A1B9A" },
  [Status.REJECTED_BY_TEAM]: { bg: "#FFEBEE", text: "#C62828" },
  [Status.RESENT]: { bg: "#E1F5FE", text: "#0277BD" },
  [Status.DONE]: { bg: "#E8F5E9", text: "#2E7D32" }
};
